import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["sx", "onClick", "icon", "as"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { CoreContainer, CoreContainerContext } from '@hub/core-container';
import { Stack } from '@hub/stack';
import { H3 } from '@hub/heading';
import { Text } from '@hub/text';
import { Cluster } from '@hub/cluster';
import { Link } from '@hub/link';
import React, { useRef, useEffect, useState, useContext } from 'react';
import { Reel } from '@hub/reel';
import { IncludeDirectLogo } from '@hub/icon/src/with-direct-logo';
import { Imposter } from '@hub/imposter';
import { ArrowLeftSmallIcon, ArrowRightSmallIcon } from '@hub/icon';
import { IconButton } from '@hub/button';
import { useToken, forwardRef, DEFAULT_BREAKPOINT, useBreakpointValue } from '@hub/design-system-base';
import debounce from 'lodash/debounce';
import { ProductCard } from '../product-card';
import { useWithIsAboveFoldProvider } from '../is-above-fold-provider';
import trackClickEvents, { categories, labels } from '../analytics/trackEvents';
import { AnalyticsListProvider } from '../analytics/analytics-list-context';
import { useAnalytics } from '../../analytics';
import { makeProductClickedForVariantGroup } from '../../analytics-segment/events/product-clicked';
import { AnalyticsImpressionTracker } from '../../analytics-observer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const buttonSizeToken = 'size-8';
export const ReelButton = forwardRef((_ref, ref) => {
  let {
    sx,
    onClick,
    icon,
    as
  } = _ref,
      rest = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/_jsx(IconButton, {
    ref: ref,
    "aria-label": rest['aria-label'],
    sx: _objectSpread({
      borderRadius: '50%',
      boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.15)',
      width: buttonSizeToken,
      height: buttonSizeToken,
      minWidth: buttonSizeToken
    }, sx),
    icon: icon,
    as: as,
    colorScheme: "white",
    variant: "solid",
    "aria-hidden": "true",
    onClick: onClick,
    "data-id": "product-deck_index_ReelButton_IconButton"
  });
});

const renderReelControls = _ref2 => {
  let {
    buttonSize,
    buttonOffsetY,
    buttonAlignmentX
  } = _ref2;
  return (// eslint-disable-next-line react/display-name
    _ref3 => {
      let {
        hasOverflowingFrames,
        children,
        scrollNext,
        scrollPrev,
        scrollPosition
      } = _ref3;
      return hasOverflowingFrames ? /*#__PURE__*/_jsx(Imposter, {
        impose: /*#__PURE__*/_jsx(ReelButton, {
          "aria-label": "Scroll to next",
          sx: {
            display: ['none', 'inherit'],
            visibility: scrollPosition === 'end' ? 'hidden' : 'visible'
          },
          icon: /*#__PURE__*/_jsx(ArrowRightSmallIcon, {
            "data-id": "product-deck_index_ReelButton_ArrowRightSmallIcon"
          }),
          onClick: () => {
            scrollNext();

            if (scrollPosition !== 'end') {
              trackClickEvents.clicked(categories.PRODUCT_REEL, labels.NEXT_CLICK);
            }
          },
          "data-id": "product-deck_index_ReelButton"
        }),
        position: "top-right",
        marginTop: "calc(".concat(buttonOffsetY, " - ").concat(buttonSize, " / 2)"),
        marginRight: buttonAlignmentX === 'center' ? "calc(".concat(buttonSize, " / -2)") : undefined,
        "data-id": "product-deck_index_ReelButton_Imposter",
        children: /*#__PURE__*/_jsx(Imposter, {
          impose: /*#__PURE__*/_jsx(ReelButton, {
            "aria-label": "Scroll to previous",
            sx: {
              display: ['none', 'inherit'],
              visibility: scrollPosition === 'start' ? 'hidden' : 'visible'
            },
            icon: /*#__PURE__*/_jsx(ArrowLeftSmallIcon, {
              "data-id": "product-deck_index_ReelButton_ArrowLeftSmallIcon"
            }),
            onClick: () => {
              scrollPrev();

              if (scrollPosition !== 'start') {
                trackClickEvents.clicked(categories.PRODUCT_REEL, labels.PREVIOUS_CLICK);
              }
            },
            "data-id": "product-deck_index_ReelButton"
          }),
          position: "top-left",
          marginTop: "calc(".concat(buttonOffsetY, " - ").concat(buttonSize, " / 2)"),
          marginLeft: buttonAlignmentX === 'center' ? "calc(".concat(buttonSize, " / -2)") : undefined,
          "data-id": "product-deck_index_ReelButton_Imposter_Imposter",
          children: children
        })
      }) : children;
    }
  );
};

const ABOVE_FOLD_THRESHOLD = 5;
export const ProductDeckReel = _ref4 => {
  let {
    products,
    productReelTitle
  } = _ref4;
  const buttonAlignmentX = useBreakpointValue(['edge', 'edge', 'center'], DEFAULT_BREAKPOINT);
  const buttonSize = useToken('sizes', buttonSizeToken);
  const container = useContext(CoreContainerContext);
  const {
    0: buttonOffsetY,
    1: setButtonOffsetY
  } = useState('50%');
  const elementButtonRelativeTo = useRef(null);
  const {
    trackEvent
  } = useAnalytics(); // NOTE: not using useLayoutEffect because we don't want to block rendering
  // just to move the button slightly

  useEffect(() => {
    var _elementButtonRelativ, _elementButtonRelativ2;

    setButtonOffsetY("".concat(Math.floor( // eslint-disable-next-line no-magic-numbers
    ((_elementButtonRelativ = elementButtonRelativeTo === null || elementButtonRelativeTo === void 0 ? void 0 : (_elementButtonRelativ2 = elementButtonRelativeTo.current) === null || _elementButtonRelativ2 === void 0 ? void 0 : _elementButtonRelativ2.clientHeight) !== null && _elementButtonRelativ !== void 0 ? _elementButtonRelativ : 0) / 2), "px")); // On some feature phones, ResizeObserver is not supported. They can just
    // stick with the above calculation instead of worrying about the resize
    // events
    // ie; avoid "ResizeObserver is not supported in KaiOS 2.5, Baidu 7.12,
    // Android UC Browser 12.12"
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    if (!window.ResizeObserver) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const resizeHandler = debounce( // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    entries => {
      // eslint-disable-next-line no-magic-numbers
      setButtonOffsetY("".concat(Math.floor(entries[0].contentRect.height / 2), "px"));
    }, 100, // Execute immediately when scrolling starts, then debounce thereafter
    {
      leading: true,
      trailing: true
    }); // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    const resizeObserver = new ResizeObserver(resizeHandler);

    if (elementButtonRelativeTo !== null && elementButtonRelativeTo !== void 0 && elementButtonRelativeTo.current) {
      resizeObserver.observe(elementButtonRelativeTo === null || elementButtonRelativeTo === void 0 ? void 0 : elementButtonRelativeTo.current);
    }

    return () => {
      // Cancel any pending debounced method calls
      resizeHandler.cancel(); // Stop observing resize events entirely

      resizeObserver.disconnect();
    };
  }, []);
  const analyticsTrackerWithFold = useWithIsAboveFoldProvider(AnalyticsImpressionTracker);
  /* eslint-disable no-magic-numbers */

  return /*#__PURE__*/_jsx(AnalyticsListProvider, {
    value: {
      list: productReelTitle,
      positions: products.reduce((acc, product, index) => {
        acc[product.sku] = index + 1;
        return acc;
      }, {})
    },
    "data-id": "product-deck_index_ReelButton_AnalyticsListProvider",
    children: /*#__PURE__*/_jsx(Reel, {
      snap: true,
      gap: ['spacing-sm', null, 'spacing-md'],
      visibleFrames: [2, 3, 4, 5],
      containerOverflow: [container.gutter, null, null, 'spacing-none'],
      peekWidth: [0.1, 0],
      shouldWrapChildren: false,
      renderControls: renderReelControls({
        buttonSize,
        buttonOffsetY,
        buttonAlignmentX
      }),
      "data-id": "product-deck_index_ReelButton_AnalyticsListProvider_Reel",
      children: products.map((product, index) => {
        const AnalyticsTrackerWithFold = analyticsTrackerWithFold(index < ABOVE_FOLD_THRESHOLD ? 'above' : 'below');
        return /*#__PURE__*/_jsx(AnalyticsTrackerWithFold, {
          child: ProductCard // Capture the ref for the first element's image
          ,
          imageRef: index === 0 ? elementButtonRelativeTo : null,
          product: product,
          onClick: () => {
            trackEvent(makeProductClickedForVariantGroup(product));
            trackClickEvents.clicked(categories.PRODUCT_REEL, "".concat(productReelTitle, " | ").concat(product.title, " | P").concat(index + 1));
          },
          onCreateImpressionWhenView: product.viewItem,
          "data-id": "product-deck_index_ReelButton_AnalyticsTrackerWithFold"
        }, "".concat(product.variantGroupCode, "-").concat(product.shop.name));
      })
    })
  });
  /* eslint-enable no-magic-numbers */
};
export const ProductDeck = _ref5 => {
  let {
    title,
    text,
    headerLinks = [],
    products,
    as,
    gutter
  } = _ref5;
  return /*#__PURE__*/_jsx(CoreContainer, {
    as: as,
    gutter: gutter,
    "data-id": "product-deck_index_ReelButton_CoreContainer",
    children: /*#__PURE__*/_jsxs(Stack, {
      gap: "spacing-sm",
      "data-id": "product-deck_index_ReelButton_CoreContainer_Stack",
      children: [/*#__PURE__*/_jsxs(Stack, {
        gap: "spacing-none",
        "data-id": "product-deck_index_ReelButton_Stack_Stack_12",
        children: [/*#__PURE__*/_jsx(H3, {
          align: "center",
          "data-id": "product-deck_index_ReelButton_Stack_H3",
          children: /*#__PURE__*/_jsx(IncludeDirectLogo, {
            title: title,
            "data-id": "product-deck_index_ReelButton_H3_IncludeDirectLogo"
          })
        }), text ? /*#__PURE__*/_jsx(Text, {
          align: "center",
          color: "quartz",
          "data-id": "product-deck_index_ReelButton_Text",
          children: text
        }) : undefined, (headerLinks === null || headerLinks === void 0 ? void 0 : headerLinks.length) > 0 ? /*#__PURE__*/_jsx(Cluster, {
          justify: "center",
          gapX: "spacing-lg",
          "data-id": "product-deck_index_ReelButton_Cluster",
          children: headerLinks.map((link, i) => /*#__PURE__*/_jsx(Text, {
            decoration: "underline",
            lineHeight: "base",
            "data-id": "product-deck_index_ReelButton_Text",
            children: /*#__PURE__*/_jsx(Link, {
              href: link.url,
              "data-id": "product-deck_index_ReelButton_Text_Link",
              children: link.title
            })
          }, i))
        }) : undefined]
      }), /*#__PURE__*/_jsx(ProductDeckReel, {
        products: products,
        productReelTitle: title,
        "data-id": "product-deck_index_ReelButton_Stack_ProductDeckReel"
      })]
    })
  });
};